import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Stack,
  LinearProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import axios from '../../utils/axios';
import { getCurrentUser } from '../../services/authService';
import locationService from '../../services/locationService';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

const UsersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const nameInputRef = useRef(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    companyId: '',
    locationId: '',
    role: 'USER',
    status: 'ACTIVE',
  });
  const [locations, setLocations] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogMessage, setLimitDialogMessage] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users?page=0&size=2000');
      const content = response.data.content || [];
      setAllUsers(Array.isArray(content) ? content.filter((u) => u.role !== 'CUSTOMER') : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Failed to load users', { variant: 'error' });
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    if (!normalizedSearch) return allUsers;
    return allUsers.filter((u) => {
      const loc = u.location?.name || '';
      const hay = [u.name, u.email, u.role, u.status, String(u.id ?? ''), loc]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(normalizedSearch);
    });
  }, [allUsers, normalizedSearch]);

  const displayedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleExportUsersExcel = useCallback(() => {
    const rows = filteredUsers.map((u) => ({
      ID: u.id,
      Name: u.name || '',
      Email: u.email || '',
      Role: u.role || '',
      Status: u.status || '',
      Location: u.location?.name || '',
    }));
    const ok = downloadXlsxFromRows(
      `users_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Users',
      rows
    );
    if (!ok) enqueueSnackbar('Nothing to export', { variant: 'info' });
  }, [filteredUsers, enqueueSnackbar]);

  const handleChangePage = (event, newPage) => {
    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1);
    setPage(Math.min(newPage, maxPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchLocations = async () => {
    try {
      const res = await locationService.list({ page: 0, size: 200, sort: 'name', direction: 'ASC' });
      setLocations(res.content || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  const handleOpenDialog = (user = null) => {
    fetchLocations();
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        companyId: user.company?.id || 1,
        locationId: user.location?.id ?? '',
        role: user.role,
        status: user.status,
      });
      setEditMode(true);
      setEditId(user.id);
    } else {
      const companyId = getCurrentUser()?.companyId ? parseInt(getCurrentUser().companyId, 10) : null;
      setFormData({
        name: '',
        email: '',
        password: '',
        companyId: companyId || '',
        locationId: '',
        role: 'USER',
        status: 'ACTIVE',
      });
      setEditMode(false);
      setEditId(null);
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!editMode && !formData.password.trim()) errors.password = 'Password is required';
    else if (!editMode && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const submitData = { ...formData };
    if (!submitData.companyId) {
      const companyId = getCurrentUser()?.companyId ? parseInt(getCurrentUser().companyId, 10) : null;
      if (companyId) submitData.companyId = companyId;
    }
    if (submitData.locationId === '') submitData.locationId = null;
    else if (submitData.locationId != null) submitData.locationId = parseInt(submitData.locationId, 10);

    try {
      setLoading(true);
      if (editMode) {
        await axios.put(`/users/${editId}`, submitData);
        enqueueSnackbar('User updated successfully', { variant: 'success' });
      } else {
        await axios.post('/users', submitData);
        enqueueSnackbar('User created successfully', { variant: 'success' });
      }
      setLoading(false);
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const msg = error.response?.data?.message || 'Failed to save user';
      if (/limit|exceed|maximum|reached/i.test(String(msg))) {
        setLimitDialogMessage(String(msg));
        setLimitDialogOpen(true);
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
      setLoading(false);
    }
  };

  const handleChangeStatus = async (userId, newStatus) => {
    try {
      setLoading(true);
      await axios.patch(`/users/${userId}/status?status=${newStatus}`);
      enqueueSnackbar('User status updated successfully', { variant: 'success' });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      enqueueSnackbar('Failed to update user status', { variant: 'error' });
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      setLoading(true);
      await axios.delete(`/users/${userId}`);
      enqueueSnackbar('User deleted successfully', { variant: 'success' });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      enqueueSnackbar('Failed to delete user', { variant: 'error' });
      setLoading(false);
    }
  };

  const getRoleChipColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'primary';
      case 'MANAGER':
        return 'secondary';
      case 'EXECUTIVE':
        return 'info';
      case 'BILLING_PERSON':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Layout>
      <PageTitle title="Users" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Users
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, email, role, status, location…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 280 }, flex: { sm: 1 } }}
            />
            <Button variant="outlined" color="primary" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportUsersExcel}>
              Export Excel
            </Button>
            <Button variant="outlined" color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchUsers} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog(null)}>
              Add User
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper variant="outlined" elevation={0} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="8%">ID</TableCell>
                    <TableCell width="18%">Name</TableCell>
                    <TableCell width="22%">Email</TableCell>
                    <TableCell width="12%">Role</TableCell>
                    <TableCell width="10%">Status</TableCell>
                    <TableCell width="18%">Location</TableCell>
                    <TableCell width="12%" align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && allUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : displayedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={user.name}>
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={user.email}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            color={getRoleChipColor(user.role)}
                            size="small"
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.status}
                            color={user.status === 'ACTIVE' ? 'success' : 'error'}
                            size="small"
                            onClick={() => handleChangeStatus(user.id, user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                            sx={{
                              height: 24,
                              cursor: 'pointer',
                              '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={user.location?.name || ''}>
                            {user.location?.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleOpenDialog(user)} size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDeleteUser(user.id)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          TransitionProps={{
            onEntered: () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  nameInputRef.current?.focus({ preventScroll: true });
                });
              });
            },
          }}
        >
          <DialogTitle>{editMode ? 'Edit User' : 'Add New User'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  inputRef={nameInputRef}
                  name="name"
                  label="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="password"
                  label={editMode ? 'New Password (leave blank to keep current)' : 'Password'}
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  error={Boolean(formErrors.password)}
                  helperText={formErrors.password}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select name="locationId" value={formData.locationId ?? ''} onChange={handleInputChange} label="Location">
                    <MenuItem value="">None (defaults to Main Location for billing)</MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select name="role" value={formData.role} onChange={handleInputChange} label="Role">
                    <MenuItem value="ADMIN">Admin</MenuItem>
                    <MenuItem value="MANAGER">Manager</MenuItem>
                    <MenuItem value="EXECUTIVE">Executive</MenuItem>
                    <MenuItem value="BILLING_PERSON">Billing Person</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select name="status" value={formData.status} onChange={handleInputChange} label="Status">
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Limit Reached</DialogTitle>
          <DialogContent>
            <Typography>{limitDialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLimitDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default UsersPage;
